import { useEffect, useMemo, useState } from 'react'
import Button from '../components/ui/Button'
import ConfirmDialog from '../components/ui/ConfirmDialog'
import DataTable from '../components/ui/DataTable'
import EmptyState from '../components/ui/EmptyState'
import ErrorState from '../components/ui/ErrorState'
import FormField from '../components/ui/FormField'
import Input from '../components/ui/Input'
import LoadingState from '../components/ui/LoadingState'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import SectionCard from '../components/ui/SectionCard'
import { ui } from '../styles/ui'
import { formatDateTime } from '../utils/date'
import { formatEmptyValue } from '../utils/displayValue'
import {
  formatSubjectDisplay,
  statusPillClass,
  ENDPOINT_GROUP_OPTIONS,
  POLICY_STATUS_OPTIONS,
  SUBJECT_TYPE_OPTIONS,
  WINDOW_SECONDS_OPTIONS,
} from './public-api-access/publicApiAccessPageUtils'
import {
  buildPolicyPayload,
  cloneEmptyPolicyForm,
  mapPolicyToForm,
  validatePolicyForm,
} from './public-api-access/publicApiLimitPolicyForm'
import {
  createPublicApiLimitPolicy,
  disablePublicApiLimitPolicy,
  listPublicApiLimitPolicies,
  updatePublicApiLimitPolicy,
} from '../services/publicApiAccessAdminService'

const initialFilters = {
  subjectType: '',
  subjectId: '',
  endpointGroup: '',
  status: '',
}

function summarizePolicies(policies) {
  return {
    total: policies.length,
    active: policies.filter((policy) => policy.status === 'active').length,
    disabled: policies.filter((policy) => policy.status === 'disabled').length,
    archived: policies.filter((policy) => policy.status === 'archived').length,
  }
}

export default function PublicApiLimitPoliciesAdmin() {
  const [filters, setFilters] = useState(initialFilters)
  const [appliedFilters, setAppliedFilters] = useState(initialFilters)
  const [refreshTick, setRefreshTick] = useState(0)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [items, setItems] = useState([])
  const [form, setForm] = useState(cloneEmptyPolicyForm())
  const [formErrors, setFormErrors] = useState({})
  const [editingId, setEditingId] = useState(null)
  const [confirmDisable, setConfirmDisable] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    listPublicApiLimitPolicies(appliedFilters)
      .then((data) => {
        if (cancelled) return
        setItems(Array.isArray(data) ? data : [])
      })
      .catch((requestError) => {
        if (cancelled) return
        setError(requestError?.message || 'Unable to load public API limit policies')
        setItems([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [appliedFilters, refreshTick])

  const summary = useMemo(() => summarizePolicies(items), [items])
  const isEditing = Boolean(editingId)

  function applyFilters() {
    setAppliedFilters({ ...filters })
  }

  function resetFilters() {
    setFilters(initialFilters)
    setAppliedFilters(initialFilters)
  }

  function startCreate() {
    setEditingId(null)
    setForm(cloneEmptyPolicyForm())
    setFormErrors({})
  }

  function startEdit(policy) {
    setEditingId(policy.id)
    setForm(mapPolicyToForm(policy))
    setFormErrors({})
  }

  async function submitPolicy(event) {
    event.preventDefault()
    const errors = validatePolicyForm(form)
    setFormErrors(errors)
    if (Object.keys(errors).length > 0) return

    const payload = buildPolicyPayload(form)
    setSaving(true)
    try {
      if (isEditing) {
        await updatePublicApiLimitPolicy(editingId, payload)
      } else {
        await createPublicApiLimitPolicy(payload)
      }
      startCreate()
      setRefreshTick((value) => value + 1)
    } catch (requestError) {
      setError(requestError?.message || 'Unable to save public API limit policy')
    } finally {
      setSaving(false)
    }
  }

  async function confirmDisablePolicy() {
    if (!confirmDisable) return
    setSaving(true)
    try {
      await disablePublicApiLimitPolicy(confirmDisable.id)
      if (editingId === confirmDisable.id) startCreate()
      setConfirmDisable(null)
      setRefreshTick((value) => value + 1)
    } catch (requestError) {
      setError(requestError?.message || 'Unable to disable public API limit policy')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <PageShell>
        <LoadingState text="Loading public API limit policies..." />
      </PageShell>
    )
  }

  if (error && items.length === 0) {
    return (
      <PageShell>
        <ErrorState text={error} action={<Button onClick={() => setRefreshTick((value) => value + 1)}>Retry</Button>} />
      </PageShell>
    )
  }

  return (
    <PageShell className="space-y-4">
      <PageHeader
        title="Public API Limit Policies"
        subtitle="Create global, anonymous, device, user, and Telegram identity policies for public recognition access."
        actions={(
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => setRefreshTick((value) => value + 1)}>Refresh</Button>
            <Button variant="secondary" onClick={startCreate}>{isEditing ? 'New policy' : 'Reset form'}</Button>
          </div>
        )}
      />

      {error && items.length > 0 ? <ErrorState text={error} /> : null}

      <SectionCard title="Filters" subtitle="Filter by subject, endpoint group, and policy status.">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <select className={ui.select} value={filters.subjectType} onChange={(event) => setFilters((current) => ({ ...current, subjectType: event.target.value }))}>
            <option value="">All subject types</option>
            {SUBJECT_TYPE_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
          <Input placeholder="Subject id" value={filters.subjectId} onChange={(event) => setFilters((current) => ({ ...current, subjectId: event.target.value }))} />
          <select className={ui.select} value={filters.endpointGroup} onChange={(event) => setFilters((current) => ({ ...current, endpointGroup: event.target.value }))}>
            <option value="">All endpoint groups</option>
            {ENDPOINT_GROUP_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
          <select className={ui.select} value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}>
            <option value="">All statuses</option>
            {POLICY_STATUS_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
          <div className="flex items-end gap-2 xl:col-span-4">
            <Button onClick={applyFilters}>Apply filters</Button>
            <Button variant="secondary" onClick={resetFilters}>Reset</Button>
          </div>
        </div>
      </SectionCard>

      <SectionCard title={isEditing ? 'Edit Policy' : 'Create Policy'}>
        <form className="grid gap-3 md:grid-cols-2 xl:grid-cols-4" onSubmit={submitPolicy}>
          <FormField label="Subject type" error={formErrors.subjectType}>
            <select className={ui.select} value={form.subjectType} onChange={(event) => setForm((current) => ({ ...current, subjectType: event.target.value }))}>
              {SUBJECT_TYPE_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
          </FormField>
          <FormField label="Subject id" error={formErrors.subjectId} helpText="Leave blank for global or anonymous fallback policies.">
            <Input value={form.subjectId} onChange={(event) => setForm((current) => ({ ...current, subjectId: event.target.value }))} />
          </FormField>
          <FormField label="Endpoint group" error={formErrors.endpointGroup}>
            <select className={ui.select} value={form.endpointGroup} onChange={(event) => setForm((current) => ({ ...current, endpointGroup: event.target.value }))}>
              {ENDPOINT_GROUP_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
          </FormField>
          <FormField label="Window seconds" error={formErrors.windowSeconds}>
            <select className={ui.select} value={form.windowSeconds} onChange={(event) => setForm((current) => ({ ...current, windowSeconds: event.target.value }))}>
              {WINDOW_SECONDS_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
          </FormField>
          <FormField label="Max requests" error={formErrors.maxRequests}><Input type="number" min="1" value={form.maxRequests} onChange={(event) => setForm((current) => ({ ...current, maxRequests: event.target.value }))} /></FormField>
          <FormField label="Max cost units" error={formErrors.maxCostUnits}><Input type="number" min="0" value={form.maxCostUnits} onChange={(event) => setForm((current) => ({ ...current, maxCostUnits: event.target.value }))} /></FormField>
          <FormField label="Max concurrent jobs" error={formErrors.maxConcurrentJobs}><Input type="number" min="0" value={form.maxConcurrentJobs} onChange={(event) => setForm((current) => ({ ...current, maxConcurrentJobs: event.target.value }))} /></FormField>
          <FormField label="Daily max requests" error={formErrors.dailyMaxRequests}><Input type="number" min="0" value={form.dailyMaxRequests} onChange={(event) => setForm((current) => ({ ...current, dailyMaxRequests: event.target.value }))} /></FormField>
          <FormField label="Monthly max requests" error={formErrors.monthlyMaxRequests}><Input type="number" min="0" value={form.monthlyMaxRequests} onChange={(event) => setForm((current) => ({ ...current, monthlyMaxRequests: event.target.value }))} /></FormField>
          <FormField label="Priority" error={formErrors.priority}><Input type="number" min="0" value={form.priority} onChange={(event) => setForm((current) => ({ ...current, priority: event.target.value }))} /></FormField>
          <FormField label="Status" error={formErrors.status}>
            <select className={ui.select} value={form.status} onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}>
              {POLICY_STATUS_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
          </FormField>
          <FormField label="Effective from" error={formErrors.effectiveFrom}><Input type="date" value={form.effectiveFrom} onChange={(event) => setForm((current) => ({ ...current, effectiveFrom: event.target.value }))} /></FormField>
          <FormField label="Effective to" error={formErrors.effectiveTo}><Input type="date" value={form.effectiveTo} onChange={(event) => setForm((current) => ({ ...current, effectiveTo: event.target.value }))} /></FormField>
          <FormField label="Reason" className="md:col-span-2 xl:col-span-4"><Input as="textarea" rows={3} value={form.reason} onChange={(event) => setForm((current) => ({ ...current, reason: event.target.value }))} /></FormField>
          <div className="md:col-span-2 xl:col-span-4 flex flex-wrap gap-2">
            <Button type="submit" disabled={saving}>{isEditing ? 'Save policy' : 'Create policy'}</Button>
            {isEditing ? <Button type="button" variant="secondary" onClick={startCreate} disabled={saving}>Cancel edit</Button> : null}
          </div>
        </form>
      </SectionCard>

      <SectionCard title="Summary">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded border border-outline bg-surface-elevated p-4">
            <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Total</div>
            <div className="mt-1 text-2xl font-semibold">{summary.total}</div>
          </div>
          <div className="rounded border border-outline bg-surface-elevated p-4">
            <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Active</div>
            <div className="mt-1 text-2xl font-semibold">{summary.active}</div>
          </div>
          <div className="rounded border border-outline bg-surface-elevated p-4">
            <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Disabled</div>
            <div className="mt-1 text-2xl font-semibold">{summary.disabled}</div>
          </div>
          <div className="rounded border border-outline bg-surface-elevated p-4">
            <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Archived</div>
            <div className="mt-1 text-2xl font-semibold">{summary.archived}</div>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Policies">
        {items.length === 0 ? (
          <EmptyState title="No policies found" description="Create the first policy or widen the filters." />
        ) : (
          <DataTable
            columns={[
              { key: 'subjectType', label: 'Subject type', sortable: true },
              { key: 'subject', label: 'Subject', sortable: true, sortAccessor: (policy) => formatSubjectDisplay(policy.subjectType, policy.subjectId) },
              { key: 'endpointGroup', label: 'Endpoint group', sortable: true },
              { key: 'windowSeconds', label: 'Window', sortable: true, sortAccessor: (policy) => Number(policy.windowSeconds || 0) },
              { key: 'maxRequests', label: 'Max requests', sortable: true, sortAccessor: (policy) => Number(policy.maxRequests || 0) },
              { key: 'maxCostUnits', label: 'Cost units', sortable: true, sortAccessor: (policy) => Number(policy.maxCostUnits || 0) },
              { key: 'maxConcurrentJobs', label: 'Concurrent jobs', sortable: true, sortAccessor: (policy) => Number(policy.maxConcurrentJobs || 0) },
              { key: 'dailyMaxRequests', label: 'Daily max', sortable: true, sortAccessor: (policy) => Number(policy.dailyMaxRequests || 0) },
              { key: 'monthlyMaxRequests', label: 'Monthly max', sortable: true, sortAccessor: (policy) => Number(policy.monthlyMaxRequests || 0) },
              { key: 'priority', label: 'Priority', sortable: true, sortAccessor: (policy) => Number(policy.priority || 0) },
              { key: 'status', label: 'Status', sortable: true },
              { key: 'effective', label: 'Effective', sortable: true, sortAccessor: (policy) => policy.effectiveFrom || '' },
              { key: 'updatedAt', label: 'Updated', sortable: true, sortAccessor: (policy) => policy.updatedAt || '' },
              { key: 'actions', label: 'Actions' },
            ]}
            rows={items}
            renderRow={(policy) => (
              <tr key={policy.id}>
                <td className={ui.tableCellText}>{policy.subjectType}</td>
                <td className={ui.tableCellText}>{formatSubjectDisplay(policy.subjectType, policy.subjectId)}</td>
                <td className={ui.tableCellText}>{policy.endpointGroup}</td>
                <td className={ui.tableCellText}>{policy.windowSeconds}</td>
                <td className={ui.tableCellText}>{policy.maxRequests}</td>
                <td className={ui.tableCellText}>{formatEmptyValue(policy.maxCostUnits)}</td>
                <td className={ui.tableCellText}>{formatEmptyValue(policy.maxConcurrentJobs)}</td>
                <td className={ui.tableCellText}>{formatEmptyValue(policy.dailyMaxRequests)}</td>
                <td className={ui.tableCellText}>{formatEmptyValue(policy.monthlyMaxRequests)}</td>
                <td className={ui.tableCellText}>{policy.priority}</td>
                <td className={ui.tableCell}><span className={statusPillClass(policy.status)}>{policy.status}</span></td>
                <td className={ui.tableCellText}>
                  <div className="flex flex-col">
                    <span>{formatEmptyValue(policy.effectiveFrom ? formatDateTime(policy.effectiveFrom) : null)}</span>
                    <span className="text-xs text-muted-foreground">{formatEmptyValue(policy.effectiveTo ? formatDateTime(policy.effectiveTo) : null)}</span>
                  </div>
                </td>
                <td className={ui.tableCellText}>{formatEmptyValue(policy.updatedAt ? formatDateTime(policy.updatedAt) : null)}</td>
                <td className={ui.tableCell}>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="secondary" onClick={() => startEdit(policy)} disabled={saving}>Edit</Button>
                    <Button size="sm" variant="danger" onClick={() => setConfirmDisable(policy)} disabled={saving || policy.status === 'disabled'}>Disable</Button>
                  </div>
                </td>
              </tr>
            )}
          />
        )}
      </SectionCard>

      <ConfirmDialog
        open={Boolean(confirmDisable)}
        title="Disable policy"
        message={confirmDisable ? `Disable policy for ${formatSubjectDisplay(confirmDisable.subjectType, confirmDisable.subjectId)}?` : 'Disable selected policy?'}
        confirmText="Disable"
        confirmVariant="danger"
        loading={saving}
        onConfirm={confirmDisablePolicy}
        onCancel={() => setConfirmDisable(null)}
      />
    </PageShell>
  )
}
